import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, ExternalLink, Sparkles, Languages } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/lib/seo";

const CATEGORIES = ["AI Governance", "AI Act", "Cybersecurity", "Digital Sovereignty", "Research", "Technology", "Company News"];
const empty = {
  title: "", excerpt: "", content: "", cover_image: "", category: "AI Governance",
  tags: "", language: "en", status: "draft", featured: false,
  author_name: "AstroLife Editorial", author_role: "AI Governance Research", seo_title: "", seo_description: "",
};

const inputCls = "w-full rounded-lg border border-navy-border bg-navy px-3 py-2.5 text-sm text-white focus:border-astro-blue focus:outline-none";
const aiBtn = "inline-flex items-center gap-1.5 rounded-full border border-astro-blue/40 bg-navy px-3 py-1.5 text-xs font-medium text-astro-light hover:bg-astro-blue hover:text-white transition-colors disabled:opacity-50";
const aiBtnSolid = "inline-flex items-center gap-1.5 rounded-full bg-astro-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-astro-light transition-colors disabled:opacity-50";

function AiRow({ label, items, ai, run }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-20 text-xs font-semibold uppercase tracking-wider text-silver/50">{label}</span>
      {items.map(([act, lbl]) => (
        <button key={act} type="button" disabled={!!ai} onClick={() => run(act)} data-testid={`ai-${act}`} className={aiBtn}>
          <Sparkles className="h-3 w-3" /> {ai === act ? "…" : lbl}
        </button>
      ))}
    </div>
  );
}

export default function BlogAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {form, id}
  const [ai, setAi] = useState(null); // current running AI action
  const [aiOutput, setAiOutput] = useState(""); // suggestions (og/internal links/related)

  const runAI = async (action, target_lang) => {
    if (!editing) return;
    setAi(action + (target_lang || ""));
    try {
      const { form } = editing;
      const { data } = await api.post("/admin/ai/generate", {
        action, title: form.title, content: form.content,
        category: form.category, language: form.language, target_lang,
      });
      const result = (data.result || "").trim();
      if (action === "draft") setEditing((e) => ({ ...e, form: { ...e.form, content: result } }));
      else if (action === "excerpt" || action === "summary") setEditing((e) => ({ ...e, form: { ...e.form, excerpt: result } }));
      else if (action === "seo") setEditing((e) => ({ ...e, form: { ...e.form, seo_description: result } }));
      else if (action === "seo_title") setEditing((e) => ({ ...e, form: { ...e.form, seo_title: result } }));
      else if (action === "keywords") setEditing((e) => ({ ...e, form: { ...e.form, tags: result } }));
      else if (action === "translate") setEditing((e) => ({ ...e, form: { ...e.form, content: result } }));
      else if (action === "faq") setEditing((e) => ({ ...e, form: { ...e.form, content: `${e.form.content}\n${result}` } }));
      else setAiOutput(result); // og, internal_links, related
      toast.success("AI content generated — review before saving.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI generation failed.");
    } finally {
      setAi(null);
    }
  };

  const translateVersion = async (target_lang) => {
    if (!editing?.id) return;
    setAi("tr" + target_lang);
    try {
      await api.post(`/admin/blog/posts/${editing.id}/translate`, { target_lang });
      qc.invalidateQueries({ queryKey: ["admin-posts"] });
      toast.success(`${target_lang === "es" ? "Spanish" : "English"} draft created — review before publishing.`);
      setEditing(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Translation failed.");
    } finally {
      setAi(null);
    }
  };

  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin-posts"],
    queryFn: async () => (await api.get("/admin/blog/posts")).data,
  });

  const openNew = () => { setAiOutput(""); setEditing({ id: null, form: { ...empty } }); };

  const openEdit = async (id) => {
    const post = (await api.get(`/admin/blog/posts/${id}`)).data;
    setAiOutput("");
    setEditing({ id, form: { ...post, tags: (post.tags || []).join(", ") } });
  };

  const save = useMutation({
    mutationFn: async ({ id, form }) => {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (id) return (await api.put(`/admin/blog/posts/${id}`, payload)).data;
      return (await api.post("/admin/blog/posts", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-posts"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      setEditing(null);
      toast.success("Article saved");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/blog/posts/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-posts"] });
      toast.success("Article deleted");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setF = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  return (
    <>
      <Seo title="Blog CMS" description="" path="/admin/blog" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black text-white">Blog CMS</h1>
          <p className="mt-2 text-silver/60">{(posts || []).length} articles</p>
        </div>
        <button onClick={openNew} data-testid="new-post-btn" className="inline-flex items-center gap-2 rounded-full bg-astro-blue px-5 py-3 text-sm font-semibold text-white hover:bg-astro-light transition-colors">
          <Plus className="h-4 w-4" /> New article
        </button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-navy-border bg-navy-surface">
        {isLoading ? (
          <div className="p-10 text-center text-silver/50">Loading…</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-navy-border text-xs uppercase tracking-wider text-silver/50">
              <tr><th className="px-5 py-4">Title</th><th className="hidden px-5 py-4 md:table-cell">Category</th><th className="px-5 py-4">Status</th><th className="px-5 py-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {(posts || []).map((p) => (
                <tr key={p.id} className="border-b border-navy-border/50 hover:bg-navy" data-testid={`post-row-${p.id}`}>
                  <td className="px-5 py-4"><div className="flex items-center gap-2"><p className="font-medium text-white">{p.title}</p><span className="rounded border border-navy-border px-1.5 py-0.5 text-[10px] font-bold uppercase text-silver/60">{p.language || "en"}</span></div>{p.featured && <span className="text-xs text-astro-light">★ Featured</span>}</td>
                  <td className="hidden px-5 py-4 text-silver/70 md:table-cell">{p.category}</td>
                  <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/20 text-slate-300"}`}>{p.status}</span></td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-silver/60 hover:text-white"><ExternalLink className="h-4 w-4" /></a>}
                      <button onClick={() => openEdit(p.id)} data-testid={`edit-post-${p.id}`} className="rounded-lg p-2 text-silver/60 hover:text-white"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => { if (window.confirm("Delete this article?")) del.mutate(p.id); }} data-testid={`delete-post-${p.id}`} className="rounded-lg p-2 text-silver/60 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="post-editor">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
          <div className="relative h-full w-full max-w-2xl overflow-y-auto border-l border-navy-border bg-navy-surface p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-white">{editing.id ? "Edit article" : "New article"}</h2>
              <button onClick={() => setEditing(null)} className="text-silver/60 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); save.mutate(editing); }}
              className="mt-6 space-y-4"
            >
              {/* AI Editorial Assistant */}
              <div className="rounded-xl border border-astro-blue/30 bg-astro-blue/5 p-4" data-testid="ai-assistant">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-astro-light" />
                  <span className="text-sm font-semibold text-white">AI Editorial Assistant</span>
                  <span className="text-xs text-silver/50">· human review required before publishing</span>
                </div>
                <div className="mt-3 space-y-2">
                  <AiRow label="Content" ai={ai} run={runAI} items={[["draft", "Generate Article"], ["faq", "Add FAQ"]]} />
                  <AiRow label="Metadata" ai={ai} run={runAI} items={[["seo_title", "SEO Title"], ["seo", "SEO Description"], ["og", "OpenGraph"], ["keywords", "Keywords"], ["excerpt", "Excerpt"]]} />
                  <AiRow label="Editorial" ai={ai} run={runAI} items={[["summary", "Executive Summary"], ["internal_links", "Internal Links"], ["related", "Related Articles"]]} />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-astro-blue/20 pt-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-silver/50">Bilingual</span>
                  <button type="button" disabled={!!ai} onClick={() => runAI("translate", "es")} data-testid="ai-translate-es" className={aiBtn}>
                    <Languages className="h-3 w-3" /> {ai === "translatees" ? "…" : "Translate body → ES"}
                  </button>
                  <button type="button" disabled={!!ai} onClick={() => runAI("translate", "en")} data-testid="ai-translate-en" className={aiBtn}>
                    <Languages className="h-3 w-3" /> {ai === "translateen" ? "…" : "Translate body → EN"}
                  </button>
                  {editing.id ? (
                    <>
                      <button type="button" disabled={!!ai} onClick={() => translateVersion("es")} data-testid="gen-es-version" className={aiBtnSolid}>
                        {ai === "tres" ? "Creating…" : "Generate Spanish version"}
                      </button>
                      <button type="button" disabled={!!ai} onClick={() => translateVersion("en")} data-testid="gen-en-version" className={aiBtnSolid}>
                        {ai === "tren" ? "Creating…" : "Generate English version"}
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-silver/40">Save the article first to auto-generate the other language.</span>
                  )}
                </div>
                {aiOutput && (
                  <div className="mt-3 rounded-lg border border-navy-border bg-navy p-3" data-testid="ai-output">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider text-silver/50">AI suggestions</span>
                      <button type="button" onClick={() => setAiOutput("")} className="text-xs text-silver/50 hover:text-white">Clear</button>
                    </div>
                    <pre className="whitespace-pre-wrap text-xs text-silver/75">{aiOutput}</pre>
                  </div>
                )}
              </div>

              <Field label="Title"><input required value={editing.form.title} onChange={(e) => setF("title", e.target.value)} data-testid="post-title" className={inputCls} /></Field>
              <Field label="SEO title"><input value={editing.form.seo_title} onChange={(e) => setF("seo_title", e.target.value)} data-testid="post-seo-title" className={inputCls} /></Field>
              <Field label="Excerpt"><textarea rows={2} value={editing.form.excerpt} onChange={(e) => setF("excerpt", e.target.value)} data-testid="post-excerpt" className={inputCls} /></Field>
              <Field label="Content (HTML)"><textarea rows={10} value={editing.form.content} onChange={(e) => setF("content", e.target.value)} data-testid="post-content" className={`${inputCls} font-mono text-xs`} /></Field>
              <Field label="Cover image URL"><input value={editing.form.cover_image} onChange={(e) => setF("cover_image", e.target.value)} data-testid="post-cover" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={editing.form.category} onChange={(e) => setF("category", e.target.value)} data-testid="post-category" className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Language">
                  <select value={editing.form.language} onChange={(e) => setF("language", e.target.value)} className={inputCls}>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                </Field>
              </div>
              <Field label="Tags (comma-separated)"><input value={editing.form.tags} onChange={(e) => setF("tags", e.target.value)} data-testid="post-tags" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Author name"><input value={editing.form.author_name} onChange={(e) => setF("author_name", e.target.value)} className={inputCls} /></Field>
                <Field label="Author role"><input value={editing.form.author_role} onChange={(e) => setF("author_role", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="SEO description"><input value={editing.form.seo_description} onChange={(e) => setF("seo_description", e.target.value)} data-testid="post-seo-description" className={inputCls} /></Field>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm text-silver/80">
                  <input type="checkbox" checked={editing.form.featured} onChange={(e) => setF("featured", e.target.checked)} data-testid="post-featured" className="h-4 w-4 accent-astro-blue" /> Featured
                </label>
                <Field label="Status" className="flex-1">
                  <select value={editing.form.status} onChange={(e) => setF("status", e.target.value)} data-testid="post-status" className={inputCls}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </Field>
              </div>

              <button type="submit" disabled={save.isPending} data-testid="save-post" className="w-full rounded-full bg-astro-blue px-6 py-3.5 font-semibold text-white hover:bg-astro-light transition-colors disabled:opacity-60">
                {save.isPending ? "Saving…" : "Save article"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-medium text-silver/80">{label}</span>
      {children}
    </label>
  );
}
